import React, { useEffect, useState } from 'react';

export default function CreditCardPage() {
  const BASE_URL = 'https://app.yaper.co/api';

  /* ===================== STATES ===================== */

  const [banks, setBanks] = useState([]);
  const [cards, setCards] = useState([]);
  const [cardNetworks, setCardNetworks] = useState([]);
  const [rewardCategories, setRewardCategories] = useState([]);

  const [bankSearch, setBankSearch] = useState('');
  const [cardSearch, setCardSearch] = useState('');
  const [networkSearch, setNetworkSearch] = useState('');
  const [keywordSearch, setKeywordSearch] = useState('');

  const [selectedKeywords, setSelectedKeywords] = useState([]);
  const [selectedCardId, setSelectedCardId] = useState(null);

  const [bankFocused, setBankFocused] = useState(false);
  const [cardFocused, setCardFocused] = useState(false);
  const [networkFocused, setNetworkFocused] = useState(false);
  const [keywordFocused, setKeywordFocused] = useState(false);

  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadedUrl, setUploadedUrl] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState('');

  const [form, setForm] = useState({
    card_type: 'creditA',
    slug: '',
    card_network_id: '',
    bank_id: '',
    url: '',
    affiliate_link: '',
    short_name: '',
    long_name: '',
    display_name: '',
    keywords: [],
  });

  const isEdit = Boolean(selectedCardId);

  /* ===================== LOAD INITIAL DATA ===================== */

  useEffect(() => {
    fetch(`${BASE_URL}/v3/bank/list`)
      .then((res) => res.json())
      .then((data) => setBanks(data?.data || data || []));
  }, []);

  useEffect(() => {
    fetch(`${BASE_URL}/v1/cards`)
      .then((res) => res.json())
      .then((data) => {
        const creditBanks = data?.cards?.credit || [];
        const flattenedCards = creditBanks.flatMap((bankObj) =>
          bankObj.cards.map((card) => ({
            ...card,
            bank_id: bankObj.bank.id,
            bank_name: bankObj.bank.name,
          }))
        );
        setCards(flattenedCards);
      });
  }, []);

  useEffect(() => {
    const loadRewards = async () => {
      try {
        const res = await fetch(`${BASE_URL}/v1/cards/reward_categories`);
        console.log(res);
        console.log(res.redirected);
        console.log(res.url);
        const data = await res.json();

        if (res.ok) {
          setRewardCategories(data?.rewards || []);
        } else {
          console.error('Reward API Error:', data);
          setRewardCategories([]);
        }
      } catch (err) {
        console.error('Failed to fetch reward categories:', err);
        setRewardCategories([]);
      }
    };

    loadRewards();
  }, []);

  /* ===================== NETWORK SEARCH ===================== */

  useEffect(() => {
    if (!networkSearch) return;
    const timeout = setTimeout(() => {
      fetch(`${BASE_URL}/v3/card_network/list?query=${networkSearch}`)
        .then((res) => res.json())
        .then((data) => setCardNetworks(data?.data || []));
    }, 300);
    return () => clearTimeout(timeout);
  }, [networkSearch]);

  /* ===================== RESET ===================== */

  // FIX: Reset all state to blank so Create mode is clean
  const handleReset = () => {
    setSelectedCardId(null);
    setCardSearch('');
    setBankSearch('');
    setNetworkSearch('');
    setSelectedKeywords([]);
    setUploadedUrl('');
    setCardNetworks([]);
    setForm({
      card_type: 'credit',
      slug: '',
      card_network_id: '',
      bank_id: '',
      url: '',
      affiliate_link: '',
      short_name: '',
      long_name: '',
      display_name: '',
      keywords: [],
    });
  };

  /* ===================== HANDLERS ===================== */

  const handleFormChange = (e) => {
    const { name, value } = e.target;

    if (name === 'display_name') {
      // FIX: properly strip trailing underscores/spaces from slug
      const autoSlug = value
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '');

      setForm((prev) => ({
        ...prev,
        display_name: value,
        slug: autoSlug,
      }));
      return;
    }

    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectBank = (bank) => {
    setForm((prev) => ({ ...prev, bank_id: bank.id }));
    setBankSearch(bank.name);
    setBankFocused(false);
  };

  const handleSelectNetwork = (network) => {
    setForm((prev) => ({ ...prev, card_network_id: network.id }));
    setNetworkSearch(network.name);
    setNetworkFocused(false);
  };

  const handleSelectCard = (card) => {
    setSelectedCardId(card.id);

    // FIX: Pre-fill bank search from loaded banks
    const matchedBank = banks.find((b) => b.id === card.bank_id);
    if (matchedBank) setBankSearch(matchedBank.name);

    // FIX: Pre-fill network search
    if (card.card_network_id) {
      fetch(`${BASE_URL}/v3/card_network/list?query=`)
        .then((res) => res.json())
        .then((data) => {
          const networks = data?.data || [];
          const matched = networks.find((n) => n.id === card.card_network_id);
          if (matched) setNetworkSearch(matched.name);
        });
    } else {
      setNetworkSearch('');
    }

    const parsedKeywords = Array.isArray(card.keywords) ? card.keywords : [];
    const matchedKeywords = rewardCategories.filter((cat) =>
      parsedKeywords.includes(cat.name)
    );
    setSelectedKeywords(matchedKeywords);

    setForm({
      card_type: 'credit',
      slug: card.slug || '',
      card_network_id: card.card_network_id || '',
      bank_id: card.bank_id || '',
      url: card.url || '',
      affiliate_link: card.affiliate_link || '',
      short_name: card.short_name || '',
      long_name: card.long_name || '',
      display_name: card.display_name || '',
      keywords: parsedKeywords,
    });

    setCardSearch(card.display_name);
    setUploadedUrl(card.url || '');
    setCardFocused(false);
  };

  /* ===================== KEYWORD TAG LOGIC ===================== */

  const handleSelectKeyword = (keyword) => {
    if (selectedKeywords.find((k) => k.id === keyword.id)) return;
    const updated = [...selectedKeywords, keyword];
    setSelectedKeywords(updated);
    setForm((prev) => ({ ...prev, keywords: updated.map((k) => k.name) }));
    setKeywordSearch('');
    setKeywordFocused(false);
  };

  const removeKeyword = (id) => {
    const updated = selectedKeywords.filter((k) => k.id !== id);
    setSelectedKeywords(updated);
    setForm((prev) => ({ ...prev, keywords: updated.map((k) => k.name) }));
  };

  /* ===================== FILE UPLOAD ===================== */

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);

    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${BASE_URL}/v1/content_pages/upload_asset`, {
      method: 'POST',
      body: formData,
    });

    const result = await response.json();
    if (response.ok) {
      setUploadedUrl(result.url);
      setForm((prev) => ({ ...prev, url: result.url }));
    } else {
      alert(result.errors || 'Upload failed');
    }
    setUploading(false);
  };

  /* ===================== SAVE ===================== */

  const handleSubmit = async () => {
    setSaving(true);

    const payload = {
      ...form,
      slug: form.slug.trim(), // FIX: always trim slug
      card_network_id: form.card_network_id || null,
      url: form.url || null,
      affiliate_link: form.affiliate_link || null,
      // FIX: only include id if editing, never send id on create
      ...(isEdit ? { id: selectedCardId } : {}),
    };

    // FIX: isEdit correctly routes to update vs create
    const url = isEdit
      ? `${BASE_URL}/v1/cards/update_data`
      : `${BASE_URL}/v1/cards`;

    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    const result = await response.json();

    if (response.ok) {
      setModalMessage(
        isEdit
          ? 'Credit Card Updated Successfully!'
          : 'Credit Card Created Successfully!'
      );
      setShowModal(true);
    } else {
      // FIX: show actual backend error message
      alert(
        `Error: ${result.errors || result.error || JSON.stringify(result)}`
      );
    }

    setSaving(false);
  };

  /* ===================== FILTERS ===================== */

  const filteredBanks = banks.filter((bank) =>
    bank.name?.toLowerCase().includes(bankSearch.toLowerCase())
  );

  const filteredCards = cards.filter((card) =>
    card.display_name?.toLowerCase().includes(cardSearch.toLowerCase())
  );

  const filteredKeywords = rewardCategories.filter((cat) =>
    cat.name?.toLowerCase().includes(keywordSearch.toLowerCase())
  );

  /* ===================== UI ===================== */

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: 24 }}>
      {/* FIX: Header shows mode + Create New button when in edit mode */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 20,
        }}
      >
        <h2 style={{ margin: 0 }}>
          {isEdit ? 'Update Credit Card' : 'Create Credit Card'}
        </h2>
        {isEdit && (
          <button onClick={handleReset} style={secondaryButtonStyle}>
            + Create New Card
          </button>
        )}
      </div>
      {/* Search existing card to edit */}
      <SearchDropdown
        placeholder="Search Existing Credit Card to Edit (leave empty to create new)..."
        value={cardSearch}
        setValue={(val) => {
          setCardSearch(val);
          // FIX: clear selectedCardId if user clears the card search
          if (!val) handleReset();
        }}
        focused={cardFocused}
        setFocused={setCardFocused}
        items={filteredCards}
        onSelect={handleSelectCard}
      />
      <SearchDropdown
        placeholder="Search Bank..."
        value={bankSearch}
        setValue={setBankSearch}
        focused={bankFocused}
        setFocused={setBankFocused}
        items={filteredBanks}
        onSelect={handleSelectBank}
      />
      <SearchDropdown
        placeholder="Search Card Network..."
        value={networkSearch}
        setValue={setNetworkSearch}
        focused={networkFocused}
        setFocused={setNetworkFocused}
        items={cardNetworks}
        onSelect={handleSelectNetwork}
      />
      {/* KEYWORD TAGS */}
      <div style={{ position: 'relative', marginBottom: 20 }}>
        <input
          placeholder="Search Reward Category..."
          value={keywordSearch}
          onChange={(e) => setKeywordSearch(e.target.value)}
          onFocus={() => setKeywordFocused(true)}
          onBlur={() => setTimeout(() => setKeywordFocused(false), 150)}
          style={inputStyle}
        />
        {keywordFocused && filteredKeywords.length > 0 && (
          <div style={dropdownStyle}>
            {filteredKeywords.map((cat) => (
              <div
                key={cat.id}
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleSelectKeyword(cat);
                }}
                style={itemStyle}
              >
                {cat.name}
              </div>
            ))}
          </div>
        )}
        <div
          style={{ marginTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap' }}
        >
          {selectedKeywords.map((k) => (
            <div key={k.id} style={tagStyle}>
              {k.name}
              <span onClick={() => removeKeyword(k.id)} style={removeTag}>
                ×
              </span>
            </div>
          ))}
        </div>
      </div>
      <input
        name="display_name"
        placeholder="Display Name"
        value={form.display_name}
        onChange={handleFormChange}
        style={inputStyle}
      />
      <input
        name="slug"
        placeholder="Slug (auto-generated)"
        value={form.slug}
        onChange={handleFormChange}
        style={inputStyle}
      />
      <input
        name="short_name"
        placeholder="Short Name"
        value={form.short_name}
        onChange={handleFormChange}
        style={inputStyle}
      />
      <input
        name="long_name"
        placeholder="Long Name"
        value={form.long_name}
        onChange={handleFormChange}
        style={inputStyle}
      />
      <input
        name="affiliate_link"
        placeholder="Affiliate Link (Paste URL here)"
        value={form.affiliate_link}
        onChange={handleFormChange}
        style={inputStyle}
      />
      <div style={{ marginTop: 20 }}>
        <input type="file" onChange={handleFileUpload} />
        {uploading && <div>Uploading...</div>}
        {uploadedUrl && <div style={{ color: 'green' }}>Uploaded ✓</div>}
      </div>
      <div style={{ display: 'flex', gap: 12, marginTop: 20 }}>
        <button onClick={handleSubmit} disabled={saving} style={buttonStyle}>
          {saving
            ? 'Saving...'
            : isEdit
              ? 'Update Credit Card'
              : 'Create Credit Card'}
        </button>
        {/* FIX: Always show reset button so user can switch to create mode anytime */}
        <button onClick={handleReset} style={secondaryButtonStyle}>
          Reset / Clear Form
        </button>
      </div>
      {showModal && (
        <div style={overlayStyle}>
          <div style={modalStyle}>
            <h3>{modalMessage}</h3>
            <button
              style={modalButton}
              onClick={() => {
                setShowModal(false);
                window.location.reload();
              }}
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ===================== SEARCH COMPONENT ===================== */

function SearchDropdown({
  placeholder,
  value,
  setValue,
  focused,
  setFocused,
  items,
  onSelect,
}) {
  return (
    <div style={{ position: 'relative', marginBottom: 20 }}>
      <input
        placeholder={placeholder}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setTimeout(() => setFocused(false), 150)}
        style={inputStyle}
      />
      {focused && items.length > 0 && (
        <div style={dropdownStyle}>
          {items.map((item) => (
            <div
              key={item.id}
              onMouseDown={(e) => {
                e.preventDefault();
                onSelect(item);
              }}
              style={itemStyle}
            >
              {item.display_name || item.name}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ===================== STYLES ===================== */

const inputStyle = {
  width: '100%',
  padding: 10,
  borderRadius: 8,
  border: '1px solid #ccc',
  marginBottom: 12,
  boxSizing: 'border-box',
};

const dropdownStyle = {
  position: 'absolute',
  top: '100%',
  left: 0,
  right: 0,
  background: '#fff',
  border: '1px solid #ddd',
  borderRadius: 8,
  maxHeight: 200,
  overflowY: 'auto',
  zIndex: 100,
};

const itemStyle = {
  padding: 10,
  cursor: 'pointer',
  borderBottom: '1px solid #eee',
};

const tagStyle = {
  background: '#e0f2fe',
  padding: '5px 10px',
  borderRadius: 20,
  display: 'flex',
  alignItems: 'center',
  gap: 6,
};

const removeTag = {
  cursor: 'pointer',
  fontWeight: 'bold',
};

const buttonStyle = {
  padding: '12px 20px',
  borderRadius: 8,
  background: '#2563eb',
  color: '#fff',
  fontWeight: 700,
  border: 'none',
  cursor: 'pointer',
};

const secondaryButtonStyle = {
  padding: '12px 20px',
  borderRadius: 8,
  background: '#6b7280',
  color: '#fff',
  fontWeight: 700,
  border: 'none',
  cursor: 'pointer',
};

const overlayStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  background: 'rgba(0,0,0,0.4)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 1000,
};

const modalStyle = {
  background: '#fff',
  padding: 30,
  borderRadius: 12,
  textAlign: 'center',
  minWidth: 300,
};

const modalButton = {
  marginTop: 20,
  padding: '10px 20px',
  borderRadius: 8,
  background: '#2563eb',
  color: '#fff',
  border: 'none',
  cursor: 'pointer',
  fontWeight: 'bold',
};
